import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useReactToPrint } from 'react-to-print';
import styled from 'styled-components';
import { useInView } from 'react-intersection-observer';
import { useSelector } from 'react-redux';
import useGtmHook from '../../util/hooks/useGtmHook';
import { APPLICATION_TERMS_VIEWED } from '../../util/gtmEvents';
import { selectApplication } from '../../store/selectors/applicationSelectors';
import { APPLICATION_TYPE } from '../../util/constants';
import Modal from '../Modal';
import ButtonPrimary from '../Buttons/Button';
import Link from '../Link';
import SectionLoader from '../SectionLoader/SectionLoader';
import Paragraph from '../Paragraph/Paragraph';
import themeColors from '../../assets/styles/themeColors';

const getButtonContainerPadding = () => '16px 0';

const getButtonBottomPositioning = (variant) => {
  if (variant === 'sm') {
    return '-35px';
  }

  if (variant === 'md') {
    return '-38px';
  }

  if (variant === 'lg') {
    return '-61px';
  }

  if (variant === 'xl') {
    return '-46px';
  }
};

const Container = styled.div`
  position: relative;

  @media only screen and (max-width: 750px) {
    padding: 0;
  }
`;

const ButtonContainer = styled.div`
  position: sticky;
  bottom: ${({ variant, noPadding }) =>
    getButtonBottomPositioning(variant, noPadding)};
  background-color: ${themeColors.colorBackgroundPrimary};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ variant, noPadding }) =>
    getButtonContainerPadding(variant, noPadding)};

  @media only screen and (max-width: 750px) {
    bottom: -32px;
  }

  @media only screen and (max-width: 500px) {
    flex-direction: column-reverse;
    gap: 25px;
  }
`;

const EndChecker = styled.div`
  opacity: 0;
`;

const NoteContainer = styled.div`
  position: sticky;
  bottom: 30px;
  padding-top: 16px;
  background-color: ${themeColors.colorBackgroundPrimary};
  display: flex;
  flex-direction: column;

  @media only screen and (max-width: 750px) {
    bottom: 42px;
  }
`;

const PrintModal = ({
  isOpen,
  variant = 'lg',
  onClose,
  scrollbarVisible,
  children,
  buttonText,
  onCloseButton,
  hidePrint,
  shouldScrollDown,
  isLoading,
  isEquityAgreement,
}) => {
  const { t } = useTranslation();
  const { gtmEvent } = useGtmHook();
  const componentRef = useRef();
  const { ref, inView } = useInView({
    threshold: 0,
  });

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });
  const Application = useSelector(selectApplication);

  const { ApplicationType } = Application;

  useEffect(() => {
    if (isOpen) {
      gtmEvent(APPLICATION_TERMS_VIEWED, {
        event_label: 'registration',
        application_type: APPLICATION_TYPE[ApplicationType],
      });
    }
  }, [isOpen]); // eslint-disable-line

  return (
    <Modal
      isOpen={isOpen}
      variant={variant}
      onClose={onClose}
      scrollbarVisible={scrollbarVisible}
    >
      <SectionLoader isLoading={isLoading}>
        <Container variant={variant} ref={componentRef}>
          {children}
          <EndChecker ref={ref} />
          {isEquityAgreement && (
            <NoteContainer>
              {!inView && (
                <Paragraph
                  color={themeColors.colorErrorMessage}
                  variant="small"
                >
                  {t('equityAgreement.note')}
                </Paragraph>
              )}
            </NoteContainer>
          )}
          <ButtonContainer variant={variant}>
            {buttonText !== null && (
              <ButtonPrimary
                disabled={shouldScrollDown && !inView}
                title={buttonText || t('common.ok')}
                onClick={onCloseButton}
              />
            )}
            {!hidePrint && (
              <Link title={t('common.printDownload')} onClick={handlePrint} />
            )}
          </ButtonContainer>
        </Container>
      </SectionLoader>
    </Modal>
  );
};

PrintModal.propTypes = {
  children: PropTypes.node,
  buttonText: PropTypes.string,
  isOpen: PropTypes.string,
  variant: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  onClose: PropTypes.func,
  scrollbarVisible: PropTypes.bool,
  onCloseButton: PropTypes.func,
  hidePrint: PropTypes.bool,
  shouldScrollDown: PropTypes.bool,
  isLoading: PropTypes.bool,
  isEquityAgreement: PropTypes.bool,
};

export default PrintModal;
